import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PopupButton } from '@typeform/embed-react';
import { isEmpty } from 'ramda';
import moment from 'moment';
import { Icon, Image, OptionsMenu, OptionButton, OptionList, OptionItem } from '..';
import { ReactComponent as RiverLogo } from '../../assets/images/image_river_logo.svg';
import {
  AuthUser,
  Dependent,
  fetchDependents,
  getAvailableFamilyPlans,
  getCompany,
  isAFamilyDependent,
  isOnFamilyPlan,
  parseFamilyMember,
} from '../../context/authContext';
import { paths } from '../../routes';
import ActivateCardPrompt from '../Modal/ActivateCardPrompt/ActivateCardPrompt';
import TransitionToPayNotice from '../Modal/TransitionToPayNotice/TransitionToPayNotice';
import { TRANSITION_TO_PAY_COMPANIES } from '../../utils/constants';
import AddFamilyMemberPrompt from '../Modal/AddFamilyMemberPrompt/AddFamilyMemberPrompt';
import AccountSwitcherPrompt from '../Modal/AccountSwitcherPrompt/AccountSwitcherPrompt';
import useFetchAsync from '../../hooks/useFetchAsync';
import getFromLocalStorage from '../../utils/getFromLocalStorage';

type NavigationProps = {
  user: AuthUser | null;
  onSignOut: () => void;
};

function Navigation({ user, onSignOut }: NavigationProps) {
  const [showActivateCardModal, setShowActivateCardModal] = React.useState(false);
  const [showTransitionToPayModal, setShowTransitionToPayModal] = React.useState(false);
  const { data, status, error, run } = useFetchAsync();
  const [showAddFamilyPlanModal, setShowAddFamilyPlanModal] = React.useState(false);
  const [showAccountSwitcherModal, setShowAccountSwitcherModal] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const sponsoredCompany = user ? getCompany(user) : null;

  const plans = user ? getAvailableFamilyPlans(user) : [];

  const hasPaymentMethod = Boolean(
    user?.subscription?.currentSubscriptionPlan &&
      (sponsoredCompany?.planPaymentOption === 'Credit Card' ||
        Boolean(user?.subscription?.last4Digits)),
  );

  const userMustTransitionToPay =
    sponsoredCompany &&
    !hasPaymentMethod &&
    moment().isBefore(moment('2025-05-01')) &&
    TRANSITION_TO_PAY_COMPANIES.includes(sponsoredCompany.id);

  /** *
   * Conditionally fetch family members if the user is on a family plan
   */
  const dependents = React.useMemo(() => {
    const tempUsrManagerAccount = JSON.parse(
      getFromLocalStorage('_river_temp_usrmanager') || '{}',
    ) as { token: string; dependents: Array<Dependent> };

    if (!data || isEmpty(tempUsrManagerAccount.dependents)) return [];

    return ((data as any)?.dependents || tempUsrManagerAccount.dependents || [])
      .map((fam: Record<string, any>) => ({
        ...parseFamilyMember(fam),
      }))
      .filter((dependent: Dependent) => {
        if (!dependent.patientDetails?.dateOfBirth) return false;
        const age = moment().diff(moment(dependent.patientDetails.dateOfBirth), 'years');
        return age <= 14 || !dependent.phoneNumber;
      }) as Array<Dependent>;
  }, [data]);

  React.useEffect(() => {
    if (!user || !isOnFamilyPlan(user)) return;
    const fetchingFamilyMembers = fetchDependents();
    run(fetchingFamilyMembers);
  }, [run, user]);

  React.useEffect(() => {
    if (params.get('s')?.includes(`all-set-${sponsoredCompany?.id}`)) {
      setShowTransitionToPayModal(true);
      return;
    }

    if (
      userMustTransitionToPay &&
      ![paths.PAYMENT_METHOD_URL_PATH, paths.SUPPORT_URL_PATH].includes(location.pathname)
    ) {
      setShowTransitionToPayModal(true);
    }
  }, [location.pathname, navigate, sponsoredCompany, params, userMustTransitionToPay]);

  const getAllSetPrice = () => {
    const price = params.get('s')?.split('-').pop();
    return price?.startsWith('$') ? price : null;
  };

  return (
    <>
      <header
        className="bg-white text-river-black shadow-sm  border-[0.5px] border-[#e9ecef] ring-[#e9ecef] ring-opacity-5 fixed top-0
      inset-x-0 z-50 flex-none min-h-[80px] tablet:min-h-[80px]"
      >
        <nav className="px-5 tablet:px-10 desktop:px-16">
          <div className="flex items-center justify-between h-20 tablet:h-24">
            <div className="flex items-center">
              <Link to={paths.HOME_URL_PATH} className="shrink-0 relative">
                <RiverLogo className="w-16" />
              </Link>
            </div>
            <div className="">
              <div className="ml-4 flex items-center space-x-8 tablet:space-x-10">
                <div className="ml-3 relative">
                  <OptionsMenu>
                    <OptionButton className="max-w-xs flex items-center text-[15px] font-poppins-medium">
                      <Image
                        className="h-[50px] w-[50px] tablet:h-12 tablet:w-12 rounded-full tablet:mr-3"
                        src={user?.signedAvatar || 'image_river_avatar_placeholder.svg'}
                        alt="user avatar"
                        bgColor={user?.avatarPlaceholderColor || ''}
                      />
                      <span className="hidden tablet:inline-flex">
                        {`${user?.firstName} ${user?.lastName}`}
                      </span>
                      <Icon id="arrowDown" className="h-5 w-5 hidden tablet:inline-flex" />
                    </OptionButton>

                    <OptionList
                      className="!min-w-[200px] mt-3 right-0 rounded-xl shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)]
                    bg-white border-[0.5px] border-[#e9ecef] ring-[#e9ecef] ring-opacity-5 focus:outline-none"
                    >
                      {!userMustTransitionToPay ? (
                        <>
                          <OptionItem
                            className="text-[15px] text-gray-700"
                            onClick={() => navigate(paths.PROFILE_URL_PATH)}
                          >
                            <Icon
                              id="iconUserProfileMenu"
                              className="w-[15px] inline-flex mr-3 text-[#676767] group-hover:fill-river-blue"
                            />
                            Profile
                          </OptionItem>

                          {dependents.length > 0 && (
                            <OptionItem
                              className="text-[15px] text-gray-700"
                              onClick={() => setShowAccountSwitcherModal(true)}
                            >
                              <Icon
                                id="iconSwitchProfileMenu"
                                className="w-[18.5px] inline-flex mr-[11px] text-[#676767] group-hover:fill-river-blue 
                            group-hover:!stroke-river-blue group-hover:!text-river-blue"
                              />
                              Switch Profile
                            </OptionItem>
                          )}

                          <OptionItem
                            className="text-[15px] text-gray-700"
                            onClick={() => setShowActivateCardModal(true)}
                          >
                            <Icon
                              id="iconCardActivate"
                              className="w-[18px] inline-flex mr-3 text-[#676767] stroke-[#676767] group-hover:fill-river-blue 
                          group-hover:stroke-river-blue group-hover:text-river-blue"
                            />
                            Activate Card
                          </OptionItem>

                          {plans && plans.length > 0 && !isAFamilyDependent(user) && (
                            <OptionItem
                              className="text-[15px] text-gray-700"
                              onClick={() => setShowAddFamilyPlanModal(true)}
                            >
                              <Icon
                                id="iconAddFamily"
                                className="w-[15px] inline-flex mr-4 text-[#676767] stroke-[#676767] group-hover:fill-river-blue group-hover:stroke-river-blue"
                              />
                              Add Family
                            </OptionItem>
                          )}
                        </>
                      ) : null}

                      {hasPaymentMethod && (
                        <OptionItem className="text-[15px] text-gray-700" onClick={() => false}>
                          <PopupButton id="Zd7M4QYX">
                            <Icon
                              id="iconReimbursement"
                              className="w-[23px] inline-flex mr-3 text-[#676767] stroke-[#676767] group-hover:fill-river-blue 
                            group-hover:stroke-river-blue group-hover:text-river-blue"
                            />
                            Reimbursement
                          </PopupButton>
                        </OptionItem>
                      )}

                      <OptionItem
                        className="text-[15px] text-gray-700"
                        onClick={() =>
                          window.open('https://www.river.health/terms-of-service', '_blank')
                        }
                      >
                        <Icon
                          id="iconUserTermsOfService"
                          className="w-[20px] inline-flex mr-3 text-[#676767] stroke-[#676767] group-hover:fill-river-blue 
                          group-hover:stroke-river-blue group-hover:text-river-blue"
                        />
                        Terms of Service
                      </OptionItem>

                      <OptionItem
                        className="text-[15px] text-gray-700"
                        onClick={() =>
                          window.open('https://www.river.health/privacy-policy', '_blank')
                        }
                      >
                        <Icon
                          id="iconUserPrivacyPolicy"
                          className="w-[20px] inline-flex mr-3 text-[#676767] stroke-[#676767] group-hover:fill-river-blue 
                          group-hover:stroke-river-blue group-hover:text-river-blue"
                        />
                        Privacy Policy
                      </OptionItem>

                      <OptionItem className="text-[15px] text-gray-700" onClick={onSignOut}>
                        <Icon
                          id="iconUserProfileMenuLogout"
                          className="w-5 inline-flex mr-3 text-[#676767] group-hover:fill-river-blue"
                        />
                        Logout
                      </OptionItem>
                    </OptionList>
                  </OptionsMenu>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>

      {user && (showActivateCardModal || location.pathname === paths.ACTIVATE_CARD_URL_PATH) && (
        <ActivateCardPrompt
          onCloseModal={() => {
            if (params.get('w') && params.get('w') === 'close') {
              window.close();
              return;
            }

            if (location.pathname === paths.ACTIVATE_CARD_URL_PATH) {
              navigate(paths.HOME_URL_PATH);
              return;
            }
            setShowActivateCardModal(false);
          }}
          user={user}
        />
      )}

      {showTransitionToPayModal && (
        <TransitionToPayNotice
          allSetPrice={getAllSetPrice()}
          onCloseModal={() => {
            if (getAllSetPrice()) {
              setShowActivateCardModal(false);
              window.location.href = location.pathname;
              // We can make the post to save to csv file
              return;
            }
            setShowTransitionToPayModal(false);
            navigate(paths.PAYMENT_METHOD_URL_PATH, {
              state: {
                action: 'transition',
                from: location.pathname,
              },
            });
          }}
        />
      )}

      {showAddFamilyPlanModal && user && (
        <AddFamilyMemberPrompt onCloseModal={() => setShowAddFamilyPlanModal(false)} user={user} />
      )}

      {showAccountSwitcherModal && user && (
        <AccountSwitcherPrompt
          onCloseModal={() => setShowAccountSwitcherModal(false)}
          user={user}
        />
      )}
    </>
  );
}

export default Navigation;
